import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, useIntl } from 'react-intl';
import { ChevronLink } from '@bibliocommons/base-links';
import { GreenBadge, DarkRedBadge, DarkGreyBadge } from '@bibliocommons/base-badges';

import Heading from 'app/components/shared/Heading';
import { UI_CONTAINER_TITLE } from 'app/constants/AnalyticsGa4Constants';
import { EVENTS_URL } from 'app/constants/EventsConstants';
import { eventShape } from 'app/shapes/events';
import ContentCard from 'app/components/cards/ContentCard';
import { getRegistrationStatusMessage } from 'app/helpers/events/utils';
import mapEventToEventCard from 'app/helpers/events/mapEventToEventCard';

import './SeriesEvents.scss';

const WAITLIST = 'waitlist';
const SERIES_COUNT = 3;

export default function SeriesEvents({
  seriesEvents,
  location,
  seriesId,
  isSeriesRegistration,
  trackEventLinkClick,
  trackViewAllEventsLinkClick
}) {
  const intl = useIntl();

  if (seriesEvents?.get('eventIds')?.isEmpty()) return null;

  let cardId = 1;
  const eventIds = seriesEvents.get('eventIds');
  const events = seriesEvents?.get('events');
  const href = `${EVENTS_URL}?series=${seriesId}`;

  const viewAllMessage = {
    id: 'events.view.upcoming',
    defaultMessage: `View all upcoming events (${seriesEvents?.get('count')})`,
    values: { count: seriesEvents?.get('count') }
  };

  const viewAllLinkText = intl.formatMessage(viewAllMessage, viewAllMessage.values);

  function renderHeading() {
    if (isSeriesRegistration) {
      return (
        <Heading className="event-section-heading" tagName="h2" size="moderate" variant>
          <FormattedMessage id="events.related.series_events" defaultMessage="Events in this series" />
        </Heading>
      );
    }
    return (
      <Heading className="event-section-heading" tagName="h2" size="moderate" variant>
        <FormattedMessage id="events.related.series" defaultMessage="This event is also offered on" />
      </Heading>
    );
  }

  function renderBadge(event, registrationStatus) {
    const isCancelled = event.getIn(['definition', 'isCancelled']);
    if (isCancelled) {
      return (
        <span className="event-status-badge">
          <DarkRedBadge size="small">
            <FormattedMessage id="cancelled" />
          </DarkRedBadge>
        </span>
      );
    }

    if (registrationStatus?.state === WAITLIST) {
      return (
        <span className="event-status-badge">
          <GreenBadge size="small">
            <FormattedMessage id="join_waitlist" />
          </GreenBadge>
        </span>
      );
    }

    if (event?.get('isFull')) {
      return (
        <span className="event-status-badge">
          <DarkGreyBadge size="small">
            <FormattedMessage id="registration_full" />
          </DarkGreyBadge>
        </span>
      );
    }

    return null;
  }

  function renderEventCard(id, eventIndex) {
    const event = events.get(id);
    const registrationStatus = getRegistrationStatusMessage(event);
    const card = mapEventToEventCard(cardId, event, location);
    cardId++;

    return (
      <li className="events-list" key={`event-card_${eventIndex}`}>
        <ContentCard
          className="event-card"
          card={card}
          hasBackground={false}
          hasBorder={false}
          hasDescription={false}
          hasType={false}
          hasTitle={false}
          dateStampSize="default"
          renderBadge={() => renderBadge(event, registrationStatus)}
          dateLink={card.get('url')}
          trackEventLinkClick={linkText =>
            trackEventLinkClick({
              linkText,
              eventData: event,
              uiContainerTitle: UI_CONTAINER_TITLE.EVENT_RELATED_SERIES
            })}
        />
      </li>
    );
  }

  return (
    <div className="cp-series-events">
      <div className="series-events-section">
        {renderHeading()}
        <div className="event-series">
          <ul className="events-list-section">{eventIds.map(renderEventCard)}</ul>
          {seriesEvents?.get('count') > SERIES_COUNT && (
            <ChevronLink
              dataKey="all-upcoming-dates"
              className="all-dates"
              href={href}
              handleClick={() =>
                trackViewAllEventsLinkClick({
                  linkText: viewAllLinkText,
                  uiContainerTitle: UI_CONTAINER_TITLE.EVENT_RELATED_SERIES
                })}
            >
              <span aria-hidden="true">{viewAllLinkText}</span>
            </ChevronLink>
          )}
        </div>
      </div>
    </div>
  );
}

SeriesEvents.propTypes = {
  seriesEvents: ImmutablePropTypes.mapContains({
    eventIds: ImmutablePropTypes.listOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    events: ImmutablePropTypes.mapOf(eventShape),
    count: PropTypes.number
  }).isRequired,
  location: ImmutablePropTypes.map,
  seriesId: PropTypes.string.isRequired,
  isSeriesRegistration: PropTypes.bool.isRequired,
  trackEventLinkClick: PropTypes.func.isRequired,
  trackViewAllEventsLinkClick: PropTypes.func.isRequired
};
